import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, SparklesIcon, GraduationCapIcon, ChevronRightIcon, BrainCircuitIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface AIAssistantProps {
  navigate: (screen: string) => void;
}
type MessageType = 'text' | 'recommendation' | 'memory-context';
interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  type: MessageType;
  productData?: any;
}
export function AIAssistant({
  navigate
}: AIAssistantProps) {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Initial messages demonstrating Agentic Memory
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    sender: 'ai',
    text: 'I noticed you were exploring GoalGuard plans yesterday and have a savings goal of ₦10M.',
    type: 'memory-context'
  }, {
    id: '2',
    sender: 'ai',
    text: "Hi Adaeze! I'm your Skydd AI Assistant. How can I help you reach your savings goals today?",
    type: 'text'
  }]);
  const suggestedQuestions = ['Tell me more about GoalGuard', 'How do I file a claim?', 'What are the contribution options?', 'Explain the life cover guarantee'];
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = (text: string = inputValue) => {
    if (!text.trim()) return;
    // Add user message
    const newUserMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: text,
      type: 'text'
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI processing and response
    setTimeout(() => {
      setIsTyping(false);
      const lowerText = text.toLowerCase();
      let aiResponse: Message[] = [];
      if (lowerText.includes('goalguard') || lowerText.includes('plan') || lowerText.includes('child education')) {
        aiResponse = [{
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: 'Based on your profile and ₦10M goal, I recommend the **GoalGuard Plus**. It offers a great balance of contribution growth and enhanced life cover.',
          type: 'text'
        }, {
          id: (Date.now() + 2).toString(),
          sender: 'ai',
          text: 'Here is the smart recommendation:',
          type: 'recommendation',
          productData: {
            name: 'GoalGuard Plus',
            premium: '₦15,000/mo',
            returns: '10% Projected'
          }
        }];
      } else if (lowerText.includes('claim')) {
        aiResponse = [{
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: "Filing a claim is easy! You can do it directly from the 'Claims' tab. You'll need to upload relevant documents (like a medical report or police report depending on the plan). Our SLA for processing is typically 48 hours.",
          type: 'text'
        }];
      } else {
        aiResponse = [{
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: "That's a great question. As your AI assistant, I can help you find the right protection plans, explain complex terms, or guide you through the claims process. What specific area would you like to explore?",
          type: 'text'
        }];
      }
      setMessages(prev => [...prev, ...aiResponse]);
    }, 1500);
  };
  return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-926">
      <TopBar title="AI Assistant" showBack onBack={() => navigate('dashboard')} data-id="element-927" />

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-32" data-id="element-928">
        <div className="space-y-6 max-w-2xl mx-auto" data-id="element-929">
          <AnimatePresence initial={false} data-id="element-930">
            {messages.map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-931">
                {msg.sender === 'ai' && <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#E5C200] flex items-center justify-center shrink-0 mr-3 mt-1 shadow-sm overflow-hidden" data-id="element-932">
                    <img src="https://cdn.magicpatterns.com/uploads/jL4v8fVsMKDyxjuaTJLwKR/default_mascott_-_black.png" alt="Skydd mascot" className="w-5 h-5 object-contain" data-id="element-933" />
                  </div>}

                <div className={`max-w-[80%] ${msg.sender === 'user' ? 'order-1' : 'order-2'}`} data-id="element-934">
                  {msg.type === 'memory-context' && <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1.5 ml-1" data-id="element-935">
                      <BrainCircuitIcon className="w-3.5 h-3.5" data-id="element-936" /> Agentic
                      Memory Active
                    </div>}

                  {msg.type === 'text' || msg.type === 'memory-context' ? <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.sender === 'user' ? 'bg-[#0D3B3B] text-white rounded-tr-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'}`} data-id="element-937">
                      {msg.text}
                    </div> : msg.type === 'recommendation' && msg.productData ? <div className="bg-white rounded-2xl p-4 border-2 border-amber-400 shadow-md shadow-amber-500/10 mt-2" data-id="element-938">
                      <div className="flex items-center gap-2 mb-3" data-id="element-939">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-940">
                          <GraduationCapIcon className="w-4 h-4" data-id="element-941" />
                        </div>
                        <div data-id="element-942">
                          <div className="text-[10px] font-bold text-amber-500 uppercase tracking-wider" data-id="element-943">
                            Smart Recommendation
                          </div>
                          <div className="font-bold text-[#0D3B3B] text-sm" data-id="element-944">
                            {msg.productData.name}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mb-4 bg-gray-50 p-2.5 rounded-xl" data-id="element-945">
                        <div data-id="element-946">
                          <div className="text-xs text-gray-500" data-id="element-947">
                            Contribution
                          </div>
                          <div className="font-bold text-[#0D3B3B]" data-id="element-948">
                            {msg.productData.premium}
                          </div>
                        </div>
                        <div className="text-right" data-id="element-949">
                          <div className="text-xs text-gray-500" data-id="element-950">Returns</div>
                          <div className="font-bold text-emerald-600" data-id="element-951">
                            {msg.productData.returns}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => navigate('product-detail', 'goalguard')} className="w-full py-2.5 bg-[#0D3B3B] text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform" data-id="element-952">
                        View Details <ChevronRightIcon className="w-4 h-4" data-id="element-953" />
                      </button>
                    </div> : null}
                </div>
              </motion.div>)}

            {isTyping && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex justify-start" data-id="element-954">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#E5C200] flex items-center justify-center shrink-0 mr-3 shadow-sm overflow-hidden" data-id="element-955">
                  <img src="https://cdn.magicpatterns.com/uploads/jL4v8fVsMKDyxjuaTJLwKR/default_mascott_-_black.png" alt="Skydd mascot" className="w-5 h-5 object-contain" data-id="element-956" />
                </div>
                <div className="bg-white border border-gray-100 px-4 py-3.5 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1.5" data-id="element-957">
                  <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-958" />
                  <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0.2
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-959" />
                  <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0.4
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-960" />
                </div>
              </motion.div>}
          </AnimatePresence>
          <div ref={messagesEndRef} data-id="element-961" />
        </div>
      </div>

      {/* Input Area */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent pt-10 pb-safe px-4" data-id="element-962">
        <div className="max-w-2xl mx-auto" data-id="element-963">
          {/* Suggested Questions */}
          <div className="flex overflow-x-auto scrollbar-hide gap-2 mb-3 pb-1" data-id="element-964">
            {suggestedQuestions.map((q, idx) => <button key={idx} onClick={() => handleSend(q)} className="whitespace-nowrap px-4 py-2 bg-white border border-gray-200 rounded-full text-xs font-medium text-[#0D3B3B] shadow-sm hover:bg-gray-50 active:scale-95 transition-all" data-id="element-965">
                {q}
              </button>)}
          </div>

          {/* Input Box */}
          <div className="relative flex items-center bg-white rounded-2xl shadow-lg border border-gray-200 p-1.5 mb-4" data-id="element-966">
            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Ask me anything about your plans..." className="flex-1 bg-transparent px-4 py-3 text-sm focus:outline-none text-[#0D3B3B] placeholder-gray-400" data-id="element-967" />
            <button onClick={() => handleSend()} disabled={!inputValue.trim() || isTyping} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${inputValue.trim() && !isTyping ? 'bg-[#0D3B3B] text-white shadow-md active:scale-95' : 'bg-gray-100 text-gray-400'}`} data-id="element-968">
              <SendIcon className="w-4 h-4 ml-0.5" data-id="element-969" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}